import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';

import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import NewBarcode from 'pages/Settings/Print/components/common/NewBarcode';
import RTLText from 'pages/Settings/Print/components/common/RTLText';
import usePrintHelper from 'pages/Settings/Print/hooks/useHelpers';

import RTLTextForPDF from '../RTLTextForPDF';

const AwbTemplate1 = (props) => {
	const {
		// OMS Correction
		isAuthorizedOMS = false,
		// Company Data
		companyLogo = null,
		// ADDRESS DATA
		receiverName,
		receiverCity,
		receiverRegion,
		receiverDistrict,
		receiverTelephone,
		receiverAddress,
		senderCity,
		senderRegion,
		senderDistrict,
		senderName,
		senderAddress,
		street,
		buildingName,
		senderTelephone,
		areaName,
		// Order Data
		orderID,
		orderDate,
		shipmentCreateDate,
		grandTotal,
		totalDue,
		shipmentNumber,
		paymentMethod,
		currency,
		weight,
		shippingDescription,
		deliveryCompanyLogo,
		useGrayScaleLogo = false,
		deliveryCompanyGrayScaleLogo = null,
		driverName,
		orderComment,
		successMessage,
		// Box Data
		boxIndex,
		// boxes = [],
		items = [],
		packageCount,
		// Return Status
		// Default
		countryISO,
		propJSON,
		reverse = false,
		ref1,
		orderPlatform,
		allItemsLength,
		sortingCode
	} = props;

	const {
		showOrderItems = true,
		showOrderNumber = true,
		showOrderNumberBarcode = true,
		showOrderDate = true,
		showPaymentMethod = true,
		showSenderAddress = true,
		showAddress = true,
		showDescription = true,
		showGrandTotal = true,
		showTotalDue = true,
		showShipmentDate = true,
		showShipmentNumber = true,
		showCity = true,
		showRegion = false,
		showDistrict = false,
		showArea = true,
		showShippingDescription = true,
		pageSize = 'A6',
		fontSize = '10px',
		fontWeight = 'regular',
		shipmentNumberBarcodeFormat = 'CODE128',
		orderNumberBarcodeFormat = 'CODE128',
		showDeliveryCompanyLogo = true,
		showDriverName = false,
		showOrderComment = false,
		showSerialNumber = false,
		showItemQuantity = false,
		language,
		shipmentNumberBarcodeFullWidth = true,
		shipmentNumberBarcodeWidth,
		shipmentNumberBarcodeHeight,
		orderNumberBarcodeFullWidth = true,
		orderNumberBarcodeWidth,
		orderNumberBarcodeHeight,
		showSuccessMessage = true,
		showOtoLogo,
		showRef1 = false,
		showSenderPhoneNumber = true,
		showReceiverPhoneNumber = true,
		showOrderPlatform = false,
		numberOfCharactersPerItem,
		numberOfShownItems,
		showSortingCode = false
	} = propJSON;

	const pdfScales = useMemo(
		() => ({
			A4: 1,
			A5: 0.9,
			A6: 0.9,
			A7: 0.8,
			'100x100': 0.7,
			'100x120': 0.7
		}),
		[]
	);

	const { t } = useTranslation('translation', {
		lng: language
	});

	const { getTemplateFontSize, getTemplateFontWeight } = usePrintHelper();

	const pageScale = pdfScales[pageSize];
	const calculatedFontSize = getTemplateFontSize(fontSize, pageScale);
	const calculatedFontWeight = getTemplateFontWeight(fontWeight);

	const styles = useMemo(
		() =>
			StyleSheet.create({
				logo: {
					display: 'flex',
					width: '100%',
					maxHeight: 100,
					height: 'auto'
				},
				dcLogo: {
					width: '30%',
					maxHeight: 100,
					height: 'auto',
					padding: 5
				},
				table: {
					fontSize: `${calculatedFontSize}pt`,
					width: '100%',
					display: 'flex',
					flexDirection: language === 'arabicValue' ? 'row-reverse' : 'row',
					flexWrap: 'wrap',
					borderStyle: 'solid',
					borderWidth: 1,
					borderRightWidth: 1,
					borderLeftWidth: 0,
					borderBottomWidth: 0,
					borderTopWidth: 1,
					borderColor: 'black'
				},
				tableRow: {
					minHeight: 15,
					height: 'auto',
					flexDirection: language === 'arabicValue' ? 'row-reverse' : 'row',
					verticalAlign: 'top',
					width: '90%',
					overflow: 'wrap'
				},
				tableCol: {
					display: 'flex',
					flex: '1 1 50%',
					flexWrap: 'wrap',
					flexDirection: language === 'arabicValue' ? 'row-reverse' : 'row',
					borderStyle: 'solid',
					borderWidth: 1,
					borderLeftWidth: 1,
					borderRightWidth: 0,
					borderTopWidth: 0,
					borderBottomWidth: 1,
					borderColor: 'black'
				},
				addressLine: {
					display: 'flex',
					flexDirection: language === 'arabicValue' ? 'row-reverse' : 'row',
					width: '100%',
					flexWrap: 'wrap',
					paddingHorizontal: 2
				},
				tableCell: {
					paddingHorizontal: 3,
					textAlign: language === 'arabicValue' ? 'right' : 'left'
				},
				bold: {
					fontWeight: 'bold'
				},
				red: {
					color: 'red',
					fontWeight: 'bold'
				},
				bottomInfo: {
					width: '100%',
					fontSize: calculatedFontSize + 1
				}
			}),
		[pageScale, language, calculatedFontSize, calculatedFontWeight]
	);

	const processedItems = useMemo(() => {
		const itemMap = items.reduce((acc, item) => {
			if (acc[item.name]) {
				acc[item.name].count += 1;
			} else {
				acc[item.name] = { ...item, count: 1 };
			}
			return acc;
		}, {});

		const itemName = (itemName) => {
			if (numberOfCharactersPerItem) {
				return itemName.length > numberOfCharactersPerItem
					? `${itemName.slice(0, numberOfCharactersPerItem)}...`
					: itemName;
			} else {
				return itemName;
			}
		};

		return Object.values(itemMap).map((item) => ({
			...item,
			displayName: item.count > 1 ? `${itemName(item.name)} (x${item.count})` : itemName(item.name)
		}));
	}, [items]);

	return (
		<>
			<View style={[styles.table]}>
				<View style={[styles.tableRow, { width: '100%' }]}>
					<View
						style={[
							{
								flex: '0 0 25%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								borderBottomWidth: 1,
								borderLeftWidth: 1
							}
						]}>
						{companyLogo && <Image src={companyLogo} style={[styles.logo]} />}
					</View>
					<View
						style={[
							{
								flex: '0 0 75%',
								borderBottomWidth: 1,
								borderLeftWidth: 1,
								display: 'flex',
								flexDirection: 'row',
								justifyContent: showOtoLogo ? 'flex-end' : 'center',
								alignItems: 'center'
							}
						]}>
						{useGrayScaleLogo && deliveryCompanyGrayScaleLogo ? (
							<Image src={deliveryCompanyGrayScaleLogo} style={[styles.dcLogo]} />
						) : (
							<>
								{showDeliveryCompanyLogo && deliveryCompanyLogo && (
									<Image src={deliveryCompanyLogo} style={[styles.dcLogo]} />
								)}
							</>
						)}
						{(showOtoLogo === undefined && countryISO === 'TR') || showOtoLogo ? (
							<Image
								src="https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png"
								style={styles.dcLogo}
							/>
						) : null}
					</View>
				</View>
				<View
					style={[
						styles.tableRow,
						{
							width: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							borderLeftWidth: 1,
							paddingTop: 5,
							paddingBottom: showSuccessMessage && !!successMessage && countryISO === 'TR' ? 2 : 5,
							paddingHorizontal: 5
						}
					]}>
					{showShipmentNumber && (
						<NewBarcode
							fullWidth={shipmentNumberBarcodeFullWidth}
							value={shipmentNumber}
							options={{
								format: shipmentNumberBarcodeFormat,
								displayValue: false,
								width: shipmentNumberBarcodeFullWidth ? 5 : shipmentNumberBarcodeWidth,
								height: shipmentNumberBarcodeFullWidth ? 100 : shipmentNumberBarcodeHeight,
								margin: 0
							}}
							textStyles={{
								fontSize: calculatedFontSize + 5
							}}
						/>
					)}
				</View>
				{showSuccessMessage && !!successMessage && countryISO === 'TR' && (
					<View
						style={[
							{
								width: '100%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								borderLeftWidth: 1,
								borderBottomWidth: 1,
								padding: 2
							}
						]}>
						<View style={[styles.bottomInfo]}>
							<Text>
								<Trans
									i18nKey={successMessage}
									t={t}
									components={{
										bold: <Text style={styles.bold} />,
										red: <Text style={styles.red} />
									}}
								/>
							</Text>
						</View>
					</View>
				)}
				{showOrderNumber && (
					<View style={[styles.tableCol]}>
						<Text style={[styles.tableCell]}>{t('order')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>{orderID}</Text>
					</View>
				)}

				{showOrderDate && (
					<View style={[styles.tableCol]}>
						<Text style={[styles.tableCell]}>{t('orderDate')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>{orderDate ?? '-'}</Text>
					</View>
				)}
				{showShipmentDate && (
					<View style={[styles.tableCol, { flex: '1 1 100%' }]}>
						<Text style={[styles.tableCell]}>{t('shipmentDate')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
							{shipmentCreateDate ?? '-'}
						</Text>
					</View>
				)}

				{showCity && (
					<View
						style={[
							styles.tableCol,
							!isAuthorizedOMS && !showRegion && !showDistrict ? { flex: '1 1 100%' } : {}
						]}>
						<Text style={[styles.tableCell]}>{t('city')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
							{receiverCity ?? senderCity}
						</Text>
					</View>
				)}

				{showRegion && (
					<View style={[styles.tableCol]}>
						<Text style={[styles.tableCell]}>{t('region')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
							{receiverRegion ?? senderRegion}
						</Text>
					</View>
				)}

				{showDistrict && (
					<View style={[styles.tableCol]}>
						<Text style={[styles.tableCell]}>{t('district')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
							{receiverDistrict ?? senderDistrict}
						</Text>
					</View>
				)}

				{showArea && isAuthorizedOMS && (
					<View style={[styles.tableCol]}>
						<Text style={[styles.tableCell]}>{t('area')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>{areaName ?? ''}</Text>
					</View>
				)}
				{showPaymentMethod && !reverse && (
					<View style={[[styles.tableCol]]}>
						<Text style={[styles.tableCell]}>{t('payment')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>{t(paymentMethod)}</Text>
					</View>
				)}

				{showTotalDue && !reverse && (
					<View style={[[styles.tableCol]]}>
						<Text style={[styles.tableCell]}>{t('totalDue')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
							{totalDue} {currency}
						</Text>
					</View>
				)}

				{showDriverName && (
					<View style={[[styles.tableCol]]}>
						<Text style={[styles.tableCell]}>{t('driverName')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
							{driverName ?? '-'}
						</Text>
					</View>
				)}

				{showOrderComment && (
					<View style={[[styles.tableCol]]}>
						<Text style={[styles.tableCell]}>{t('orderComments')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
							{orderComment ?? '-'}
						</Text>
					</View>
				)}

				{/* Address Zone */}
				{showSenderAddress && (
					<>
						<View
							style={[
								styles.tableCol,
								{ flex: '1 1 100%', flexDirection: 'column', flexWrap: 'nowrap' }
							]}>
							<View style={[styles.addressLine]}>
								<Text>{t('from/sender')}</Text>
								<Text>{' : '}</Text>
								<Text style={[{ calculatedFontWeight }]}>{senderName ?? null}</Text>
							</View>
							<View style={[styles.addressLine]}>
								<Text>{t('address')}</Text>
								<Text>{' : '}</Text>
								<Text style={{ calculatedFontWeight }}>
									<RTLTextForPDF text={senderAddress} />
								</Text>
							</View>

							{showSenderPhoneNumber && (
								<View style={styles.addressLine}>
									<Text>{t('phone')}</Text>
									<Text>{' : '}</Text>
									<Text style={{ calculatedFontWeight }}>{senderTelephone ?? null}</Text>
								</View>
							)}
						</View>
					</>
				)}
				{showAddress && (
					<>
						<View
							style={[
								styles.tableCol,
								{ flex: '1 1 100%', flexDirection: 'column', flexWrap: 'nowrap' }
							]}>
							<View style={[styles.addressLine]}>
								<Text>{t('to/recipient')}</Text>
								<Text>{' : '}</Text>
								<Text style={[{ calculatedFontWeight }]}>{receiverName ?? null}</Text>
							</View>
							<View style={[styles.addressLine]}>
								<Text>{t('address')}</Text>
								<Text>{' : '}</Text>
								<Text style={{ calculatedFontWeight }}>
									<RTLTextForPDF text={receiverAddress} />
									{street ? `, ${street}` : ''}
									{buildingName ? `, ${buildingName}` : ''}
								</Text>
							</View>
							{showReceiverPhoneNumber && (
								<View style={styles.addressLine}>
									<Text>{t('phone')}</Text>
									<Text>{' : '}</Text>
									<Text style={{ calculatedFontWeight }}>{receiverTelephone ?? null}</Text>
								</View>
							)}
						</View>
					</>
				)}
				{/* Address Zone */}
				{showRef1 && (
					<View style={[styles.tableCol, { flex: '1 1 100%' }]}>
						<Text style={[styles.tableCell]}>{t('reference')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>{ref1}</Text>
					</View>
				)}
				{showShippingDescription && (
					<View style={[styles.tableCol, { flex: '1 1 100%' }]}>
						<Text style={[styles.tableCell]}>{t('shippingDescription')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
							{shippingDescription ?? '-'}
						</Text>
					</View>
				)}

				{showGrandTotal && (
					<View style={[styles.tableCol, { flex: '1 1 33.333%' }]}>
						<Text style={[styles.tableCell]}>{t('grandTotal')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
							{grandTotal} {currency}
						</Text>
					</View>
				)}
				{packageCount > 0 && (
					<View style={[styles.tableCol, { flex: '1 1 33.333%' }]}>
						<Text style={[styles.tableCell]}>{t('weight')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell]}>{weight ?? '-'} kg</Text>
					</View>
				)}

				<View style={[styles.tableCol, { flex: '1 1 33.333%' }]}>
					<Text style={[styles.tableCell, { borderRightWidth: !packageCount ? 1 : 0 }]}>
						{t('numberOfPieces')}
					</Text>
					<Text>{' : '}</Text>
					<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
						{boxIndex + 1} / {packageCount}
					</Text>
				</View>
				{showDescription && (
					<View style={[styles.tableCol, { flex: '1 1 100%' }]}>
						<Text style={[styles.tableCell]}>{t('itemDescription')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>
							{!!items?.length && items?.some((item) => !!item.description)
								? items?.map((item, index) => (
										<React.Fragment key={index}>
											<Text>{!!item?.description && item.description}</Text>
											{index !== items?.length - 1 && !!item?.description?.length && (
												<Text>,&nbsp;</Text>
											)}
										</React.Fragment>
									))
								: orderComment}
						</Text>
					</View>
				)}
				{showSortingCode && (
					<View style={[styles.tableCol, { flex: '1 1 100%' }]}>
						<Text
							style={[
								styles.tableCell,
								{ fontWeight: calculatedFontWeight, fontSize: calculatedFontSize + 7 }
							]}>
							{sortingCode}
						</Text>
					</View>
				)}
				{showOrderNumberBarcode && countryISO !== 'TR' && (
					<View
						style={{
							width: '100%',
							borderLeftWidth: 1,
							borderBottomWidth: 1,
							paddingHorizontal: 5
						}}>
						<NewBarcode
							fullWidth={orderNumberBarcodeFullWidth}
							value={orderID}
							options={{
								width: orderNumberBarcodeFullWidth ? 5 : orderNumberBarcodeWidth,
								height: orderNumberBarcodeFullWidth ? 100 : orderNumberBarcodeHeight,
								format: orderNumberBarcodeFormat,
								displayValue: false,
								margin: 0
							}}
							textStyles={{
								fontSize: calculatedFontSize + 5
							}}
						/>
					</View>
				)}
				{showOrderItems && (
					<View
						style={[
							styles.tableRow,
							{
								width: '100%',
								borderStyle: 'solid',
								borderWidth: 1,
								borderLeftWidth: 1,
								borderRightWidth: 0,
								borderTopWidth: 0,
								borderBottomWidth: 1
							}
						]}>
						<Text style={[styles.tableCell]}>
							{showSerialNumber ? t('itemNamesAndSerialNumbers') : t('itemNames')}
							{numberOfShownItems &&
								numberOfShownItems > 0 &&
								numberOfShownItems < allItemsLength &&
								` (${numberOfShownItems} ${t('of')} ${allItemsLength})`}
						</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell]}>
							{!!processedItems?.length &&
								processedItems?.map((item, index) => (
									<React.Fragment key={index}>
										<RTLText
											key={index}
											style={[{ fontWeight: calculatedFontWeight }, styles.tableCell]}
											reverse={true}>
											{item?.displayName ?? '-'}
										</RTLText>
										{showItemQuantity && item?.quantity && (
											<RTLText
												key={index}
												style={[{ fontWeight: calculatedFontWeight }, styles.tableCell]}
												reverse={true}>
												{` (${item.quantity})`}
											</RTLText>
										)}

										{showSerialNumber && item?.serialNumber ? (
											<RTLText
												key={index}
												style={[{ fontWeight: calculatedFontWeight }, styles.tableCell]}
												reverse={true}>
												{` / ${item.serialNumber}`}
											</RTLText>
										) : null}

										{index === numberOfShownItems - 1 && numberOfShownItems < allItemsLength
											? ', ...'
											: '\n'}
									</React.Fragment>
								))}
						</Text>
					</View>
				)}
				{showOrderPlatform && (
					<View style={[styles.tableCol]}>
						<Text style={[styles.tableCell]}>{t('orderPlatform')}</Text>
						<Text>{' : '}</Text>
						<Text style={[styles.tableCell, { fontWeight: calculatedFontWeight }]}>{orderPlatform}</Text>
					</View>
				)}
			</View>
		</>
	);
};

export default AwbTemplate1;
